type Props = {
  /** Posted limit in km/h, or null if unknown. */
  kmh: number | null;
  /** True when the driver is currently over the limit — adds a soft alarm halo. */
  over?: boolean;
  /** Pixel size of the sign. */
  size?: number;
};

/**
 * SpeedLimitSign — European-style circular road sign. White disc, red
 * ring, big black number. We keep the iconography authentic so it reads
 * at a glance even if the UI styling changes. An outer accent glow ties
 * it gently to the rest of the dashboard.
 */
export function SpeedLimitSign({ kmh, over = false, size = 84 }: Props) {
  const empty = kmh == null;
  const label = empty ? "—" : String(kmh);
  // 3-digit numbers (e.g. 130) need a smaller font so they don't crowd the ring.
  const digits = label.length;
  const fontPx = digits >= 3 ? size * 0.36 : size * 0.46;

  return (
    <div
      className={`limit-sign ${empty ? "is-empty" : ""} ${over ? "is-over" : ""}`}
      style={{ width: size, height: size }}
      aria-label={empty ? "Speed limit unknown" : `Speed limit ${kmh} km/h`}
      role="img"
    >
      <svg viewBox="0 0 100 100" width={size} height={size} aria-hidden>
        {/* drop shadow / lift from the panel */}
        <defs>
          <filter id="ls-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="2" />
            <feOffset dy="2" />
            <feComponentTransfer><feFuncA type="linear" slope="0.45" /></feComponentTransfer>
            <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
        {/* white disc */}
        <circle cx="50" cy="50" r="46" fill="#ffffff" filter="url(#ls-shadow)" />
        {/* red ring */}
        <circle cx="50" cy="50" r="40" fill="none" stroke="#d62828" strokeWidth="11" />
        {/* number */}
        <text
          x="50"
          y="50"
          textAnchor="middle"
          dominantBaseline="central"
          fontFamily='"Open Sans", "Calibri", system-ui, sans-serif'
          fontWeight="800"
          fontSize={fontPx}
          fill="#0a0c12"
          letterSpacing="-1.5"
        >
          {label}
        </text>
      </svg>
    </div>
  );
}
